import React from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@apollo/react-hooks';
import {
  change,
  destroy,
  initialize,
  updateSyncErrors,
  submit
} from 'redux-form';
import { connect } from 'react-redux';

import { clearDeletedFields } from 'actions/activityAndAccommodation';

import { getPreparedDataToCreateActivity } from 'utils/accommodations-and-activities/prepareActivityData';
import {
  isCurrentStepAvailable,
  isNextStepAvailable
} from 'utils/accommodations-and-activities/activityNextStepAvailableHander';
import {
  discardSection,
  isDiscardChanges,
  isDiscardSection,
  discardChanges
} from 'utils/accommodations-and-activities/activityDiscardChangesHandler';
import { isActivityFormDone } from 'utils/accommodations-and-activities/activityFormDoneHandler';
import {
  addAddOnsLanguage,
  deleteAddOnsLanguage
} from 'utils/accommodations-and-activities/addOnsLanguagesHandler';

import ConfirmContext from 'contexts/ConfirmContext';

import { CREATE_ACTIVITY, UPDATE_ACTIVITY } from 'graphql/mutations/activity';

import {
  TabsMenu,
  TabsPanel,
  RadioBtnTabsMenu,
  GeneralCreate,
  GeneralCreateSecondLanguage,
  DescriptionCreate,
  ScheduleCreate,
  PricesCreate,
  AddOns,
  RelatedAccommodations,
  Integration,
  CircularLoader,
  AppHeader
} from 'components';
import ActivityFuturePrices from 'components/future-price/ActivityFuturePrice';

import {
  DEFAULT_LANGUAGE,
  SCHEDULE_SECTION,
  ACTIVITY_FORMS_ORDER,
  INITIAL_ACTIVITY_FORM_DATA,
  ACTIVITY_FORMS_NUMBERS,
  ACTIVITIES_TAB
} from './constants';

import { useStyles } from './styles';
import { getFileByUrl } from '../../utils/files/handleFIles';
import { CANCEL_ACTIVITY_BOOKING } from '../../graphql/mutations/booking';


const CreateActivity = ({
  data,
  scheduleActiveSubTabNumber,
  pricesActiveSubTabNumber,
  existingFutureRatePlanIds,
  loading,
  isCopy,
  bookDates,
  ...props
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const [createActivity] = useMutation(CREATE_ACTIVITY);
  const [updateActivity] = useMutation(UPDATE_ACTIVITY);
  const [cancelActivityBooking] = useMutation(CANCEL_ACTIVITY_BOOKING);

  const {
    changeFormFieldValue,
    destroyForm,
    initializeForm,
    updateFormErrors,
    submitForm,
    deletedAddOns,
    deletedRelatedAccommodations,
    deletedIntegrations,
    clearDeletedFields
  } = props;

  const [initialFormData, setInitialFormData] = React.useState({
    ...INITIAL_ACTIVITY_FORM_DATA
  });

  const [isLoading, setIsLoading] = React.useState(true);
  const [isUpdate, setIsUpdate] = React.useState(false);
  const [isVisitedPrices, setIsVisitedPrices] = React.useState(false);
  const [isSaveUnsavedChanges, setIsSaveUnsavedChanges] = React.useState(false);

  const [savedForms, setSavedForms] = React.useState({});
  const [dynamicInitialData, setDynamicInitialData] = React.useState({});

  const [languages, setLanguages] = React.useState([DEFAULT_LANGUAGE]);
  const [selectedLanguage, setSelectedLanguage] = React.useState(
    DEFAULT_LANGUAGE
  );

  const [activeTab, setActiveTab] = React.useState(
    ACTIVITY_FORMS_NUMBERS.generalForm
  );
  const [scheduleActiveSubTab, setScheduleActiveSubTab] = React.useState(0);
  const [pricesActiveSubTab, setPricesActiveSubTab] = React.useState(0);

  const futurePricesInitialValues = React.useMemo(() => {
    const values = {};
    existingFutureRatePlanIds?.forEach(id => {
      values[`selected_${id}`] = true;
    });
    return values;
  }, [existingFutureRatePlanIds]);

  React.useEffect(() => {
    destroySecondLanguageForms();
    clearDeletedFields();
    setLanguages([DEFAULT_LANGUAGE]);
    setInitialFormData({ ...INITIAL_ACTIVITY_FORM_DATA });
    if (loading) {
      setIsLoading(true);
      return;
    }
    if (data) {
      setIsUpdate(true);
      setIsVisitedPrices(true);
      const scheduleTab =
        ACTIVITY_FORMS_ORDER[ACTIVITY_FORMS_NUMBERS.scheduleForm][
          scheduleActiveSubTabNumber
        ];
      const activityType = data['generalForm'].activityType;
      let pricesTabIndex =
        activityType === 'Open'
          ? pricesActiveSubTabNumber + 1
          : pricesActiveSubTabNumber;
      if (scheduleTab === 'schedulePassForm') {
        if (activityType === 'Open') {
          pricesTabIndex = 0;
        } else {
          if (pricesActiveSubTab) {
            pricesTabIndex = 1;
          } else {
            pricesTabIndex = 0;
          }
        }
      }
      setSavedForms({
        ...data,
        scheduleTab: scheduleTab,
        pricesTab:
          ACTIVITY_FORMS_ORDER[ACTIVITY_FORMS_NUMBERS.pricesForm][
            scheduleActiveSubTabNumber
          ][pricesTabIndex]
      });
      setScheduleActiveSubTab(scheduleActiveSubTabNumber);
      setPricesActiveSubTab(pricesActiveSubTabNumber);
    } else {
      setSavedForms({
        scheduleTab: 'scheduleDateForm',
        pricesTab: 'pricesDailyForm'
      });
    }
    let newInitialFormData = {
      ...INITIAL_ACTIVITY_FORM_DATA,
      ...data
    };
    setInitialFormData(newInitialFormData);
    initializeForms(newInitialFormData);
    setIsLoading(false);
  }, [loading]);

  const initializeForms = initialFormsData => {
    if (
      initialFormsData['generalForm-Norwegian'] &&
      initialFormsData['generalForm-Norwegian'].title
    ) {
      setLanguages([...languages, 'Norwegian']);
    }

    Object.keys(initialFormsData).map(field => {
      updateFormErrors(field, {});
      initializeForm(field, initialFormsData[field]);
    });
  };

  const destroySecondLanguageForms = () => {
    const forms = ['generalForm-Norwegian', 'descriptionForm-Norwegian'];
    destroyForm(forms);
  };

  const replacePhotosToFiles = async data => {
    if (!!data.addOns) {
      for (const addOn of data.addOns) {
        if (!addOn.photo || typeof addOn.photo !== 'string') continue;
        addOn.photo = await getFileByUrl(addOn.photo);
      }
    }

    if (!data.photo) {
      data.photo = await getFileByUrl(
        props['generalForm'].values.coverPhoto.image
      );
    }

    return data;
  };

  const onCreateActivityHandler = async () => {
    const pricesTabIndex =
      props['generalForm'].values.activityType === 'Open'
        ? pricesActiveSubTab + 1
        : pricesActiveSubTab;

    const activePricesForm =
      props[
        ACTIVITY_FORMS_ORDER[ACTIVITY_FORMS_NUMBERS.pricesForm][
          scheduleActiveSubTab
        ][pricesTabIndex]
      ];

    if (activePricesForm?.syncErrors) {
      enqueueSnackbar(
        t(
          'You should complete and save all previous steps before you can save'
        ),
        {
          variant: 'info',
          autoHideDuration: 5000
        }
      );
      return;
    }

    setIsLoading(true);

    let data = getPreparedDataToCreateActivity(
      props,
      scheduleActiveSubTab,
      pricesActiveSubTab,
      languages,
      isUpdate,
      deletedAddOns,
      deletedRelatedAccommodations,
      deletedIntegrations,
      isCopy
    );
    const futureRatePlanIds = data.activity.futureRatePlanIds;

    if (isUpdate && !isCopy) {
      const promises = data.deactivationPeriods.map(({ id }) =>
        id?.includes('new-one')
          ? Promise.resolve()
          : cancelActivityBooking({
              variables: {
                ids: [id]
              }
            })
      );

      await Promise.all(promises);

      updateActivity({
        variables: {
          ...data,
          activity: {
            ...data.activity,
            futureRatePlanIds: futureRatePlanIds
          },

          deactivationPeriods: data.deactivationPeriods
            .filter(({ isCanceled }) => !isCanceled)
            .map(({ id, ...date }) => date)
        }
      }).then(
        () => {
          enqueueSnackbar(t('Activity has been successfully updated'), {
            variant: 'success'
          });
          history.push({
            pathname: '/accommodations-and-activities',
            state: { tabNumber: ACTIVITIES_TAB }
          });
        },
        () => {
          enqueueSnackbar(t('Something was wrong'), {
            variant: 'error'
          });
          setIsLoading(false);
        }
      );
      return;
    }

    if (isCopy) {
      data = await replacePhotosToFiles(data);
    }

    createActivity({
      variables: {
        ...data,
        activity: {
          ...data.activity,
          futureRatePlanIds: futureRatePlanIds
        },

        deactivationPeriods: data.deactivationPeriods
          .filter(({ isCanceled }) => !isCanceled)
          .map(({ id, ...date }) => date)
      }
    }).then(
      () => {
        enqueueSnackbar(
          t(`Activity has been successfully ${isCopy ? 'copied' : 'created'}`),
          {
            variant: 'success'
          }
        );
        history.push({
          pathname: '/accommodations-and-activities',
          state: { tabNumber: ACTIVITIES_TAB }
        });
      },
      () => {
        enqueueSnackbar(t('Something was wrong'), {
          variant: 'error'
        });
        setIsLoading(false);
      }
    );
  };

  const onActiveTabChangeHandler = (event, newActiveTab) => {
    if (
      !isNextStepAvailable(
        props,
        languages,
        newActiveTab,
        savedForms,
        scheduleActiveSubTab,
        pricesActiveSubTab
      )
    ) {
      enqueueSnackbar(
        t(
          'You should complete and save all previous steps before you can go to this step'
        ),
        {
          variant: 'info',
          autoHideDuration: 5000
        }
      );
      return false;
    }
    let formName = ACTIVITY_FORMS_ORDER[activeTab];

    if (activeTab === ACTIVITY_FORMS_NUMBERS.scheduleForm) {
      formName = formName[scheduleActiveSubTab];
    } else if (activeTab === ACTIVITY_FORMS_NUMBERS.pricesForm) {
      if (
        props.generalForm &&
        props.generalForm.values.activityType === 'Open'
      ) {
        let pricesActiveSubTabIsOpen = pricesActiveSubTab;
        pricesActiveSubTabIsOpen++;
        formName = formName[scheduleActiveSubTab][pricesActiveSubTabIsOpen];
      } else {
        formName = formName[scheduleActiveSubTab][pricesActiveSubTab];
      }
    }

    if (
      isDiscardSection(
        activeTab,
        newActiveTab,
        scheduleActiveSubTab,
        pricesActiveSubTab,
        savedForms
      )
    ) {
      onDiscardChangesHandler(formName, newActiveTab, true);
      return;
    }

    if (
      isDiscardChanges(
        props,
        formName,
        activeTab,
        newActiveTab,
        savedForms,
        initialFormData,
        dynamicInitialData,
        languages
      )
    ) {
      onDiscardChangesHandler(formName, newActiveTab, false);
      return;
    }

    setActiveTab(newActiveTab);
  };

  const onDiscardChangesHandler = (
    formName,
    newActiveTab,
    isDiscardSection
  ) => {
    confirm({
      title: 'You have unsaved changes',
      description: t('Are you sure you want to discard the changes?'),
      confirmationText: 'Save',
      cancellationText: 'Discard'
    })
      .then(() => {
        setIsSaveUnsavedChanges(true);
        submitForm(formName);

        if (
          !isCurrentStepAvailable(
            props,
            activeTab,
            scheduleActiveSubTab,
            pricesActiveSubTab
          )
        ) {
          setIsSaveUnsavedChanges(false);
          return;
        }

        if (
          isNextStepAvailable(
            props,
            languages,
            newActiveTab,
            savedForms,
            scheduleActiveSubTab,
            pricesActiveSubTab
          )
        ) {
          setActiveTab(newActiveTab);
        } else {
          enqueueSnackbar(
            t(
              'You should complete and save all previous steps before you can go to this step'
            ),
            {
              variant: 'info',
              autoHideDuration: 5000
            }
          );
        }
        setIsSaveUnsavedChanges(false);
      })
      .catch(() => {
        if (isDiscardSection) {
          discardSection(
            activeTab,
            newActiveTab,
            setScheduleActiveSubTab,
            setPricesActiveSubTab,
            setActiveTab,
            formName,
            initializeForm,
            savedForms,
            initialFormData,
            dynamicInitialData
          );
          return;
        }
        discardChanges(
          languages,
          formName,
          initializeForm,
          savedForms,
          dynamicInitialData,
          initialFormData,
          newActiveTab,
          activeTab,
          setActiveTab
        );
      });
  };

  const addLanguage = newItem => {
    setLanguages([...languages, newItem]);
    addAddOnsLanguage(
      props,
      newItem,
      changeFormFieldValue,
      updateFormErrors,
      setSavedForms,
      savedForms
    );
  };

  const deleteLanguage = deletedItem => {
    confirm({
      title: 'Are you sure you want to delete this tab?',
      description: t('All related tabs in this language will also be deleted.'),
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        setIsSaveUnsavedChanges(true);

        const languagesCopy = [...languages];
        const updatedLanguages = languagesCopy.filter(
          item => item !== deletedItem
        );
        setSelectedLanguage(DEFAULT_LANGUAGE);
        setLanguages(updatedLanguages);

        deleteAddOnsLanguage(
          props,
          deletedItem,
          changeFormFieldValue,
          activeTab,
          setActiveTab,
          setSavedForms,
          savedForms,
          updateFormErrors,
          submitForm
        );
        deleteFormLanguage(deletedItem);

        setIsSaveUnsavedChanges(false);
      })
      .catch(() => {});
  };

  const selectLanguage = item => {
    setSelectedLanguage(item);
  };

  const deleteFormLanguage = deletedLanguage => {
    const general = `generalForm-${deletedLanguage}`;
    const description = `descriptionForm-${deletedLanguage}`;

    destroyForm([general, description]);
    setSavedForms({
      ...savedForms,
      [general]: {},
      [description]: {}
    });
  };

  const onChangeActivityTypeHandler = savedFormsCopy => {
    const pricesTabIndex =
      props['generalForm'].values.activityType === 'Open' ? 1 : 0;
    setPricesActiveSubTab(0);
    setSavedForms({
      ...savedFormsCopy,
      pricesTab:
        ACTIVITY_FORMS_ORDER[ACTIVITY_FORMS_NUMBERS.pricesForm][
          scheduleActiveSubTab
        ][pricesTabIndex]
    });
  };

  const getPropsForTabsWithLanguages = () => {
    return {
      selectedLanguage: selectedLanguage,
      languages: languages,
      addLanguage: addLanguage,
      deleteLanguage: deleteLanguage,
      selectLanguage: selectLanguage
    };
  };

  const [submitButton, setSubmitButton] = React.useState('');

  const onGeneralAndDescFormSubmitHandler = (
    languagesLength,
    secondLanguage,
    currentFormName,
    secondFormName
  ) => {
    const forms = [currentFormName];
    let savedFormsCopy = { ...savedForms };
    let isSetPriceActiveSubTab = false;

    if (languagesLength > 1) forms.push(`${currentFormName}-${secondLanguage}`);

    for (let i in forms) {
      if (!forms.hasOwnProperty(i)) continue;

      const formName = forms[i];
      const form = { ...props[formName] };

      if (!form || !form.values || form.syncErrors) {
        enqueueSnackbar(
          t(
            'Please fill in all the required fields correctly on the current step for all languages'
          ),
          {
            variant: 'warning'
          }
        );
        return;
      }

      if (formName === 'generalForm') {
        if (savedForms[formName]) {
          if (savedForms[formName].activityType !== form.values.activityType) {
            isSetPriceActiveSubTab = true;
          }
        } else if (
          initialFormData[formName].activityType !== form.values.activityType
        ) {
          isSetPriceActiveSubTab = true;
        }
      }

      savedFormsCopy = {
        ...savedFormsCopy,
        [formName]: { ...form.values }
      };
    }

    if (isSetPriceActiveSubTab && isVisitedPrices && isSaveUnsavedChanges) {
      onChangeActivityTypeHandler(savedFormsCopy, secondFormName);
      return;
    }

    if (isSetPriceActiveSubTab && isVisitedPrices && !isSaveUnsavedChanges) {
      confirm({
        title: 'Are you sure you want to change the activity type?',
        description:
          'Note, that previously filled in data on the Prices may be reset.',
        confirmationText: 'Change',
        cancellationText: 'Back'
      })
        .then(() => {
          onChangeActivityTypeHandler(savedFormsCopy);
          setActiveTab(ACTIVITY_FORMS_NUMBERS[secondFormName]);
        })
        .catch(() => {});
      return;
    }

    setSavedForms({
      ...savedFormsCopy
    });

    if (submitButton === 'save' && !isSaveUnsavedChanges) {
      onCreateActivityHandler();
    }
    if (submitButton === 'next' && !isSaveUnsavedChanges) {
      setActiveTab(ACTIVITY_FORMS_NUMBERS[secondFormName]);
    }
  };

  const getGeneralCreateTab = () => {
    const languagesLength = languages.length;
    const secondLanguage = languages[languagesLength - 1];

    const generalFormProps = {
      onSubmit: () =>
        onGeneralAndDescFormSubmitHandler(
          languagesLength,
          secondLanguage,
          'generalForm',
          'descriptionForm'
        ),
      ...getPropsForTabsWithLanguages()
    };

    return (
      <div>
        <div hidden={selectedLanguage !== DEFAULT_LANGUAGE}>
          <GeneralCreate
            {...generalFormProps}
            isUpdate={isUpdate}
            setSubmitButton={setSubmitButton}
            bookDates={bookDates}
          />
        </div>
        {languagesLength > 1 && (
          <div hidden={selectedLanguage === DEFAULT_LANGUAGE}>
            <GeneralCreateSecondLanguage
              {...generalFormProps}
              form={`generalForm-${secondLanguage}`}
              isUpdate={isUpdate}
              setSubmitButton={setSubmitButton}
            />
          </div>
        )}
      </div>
    );
  };

  const getDescriptionCreateTab = () => {
    const languagesLength = languages.length;
    const secondLanguage = languages[languagesLength - 1];

    const descriptionFormProps = {
      onSubmit: () =>
        onGeneralAndDescFormSubmitHandler(
          languagesLength,
          secondLanguage,
          'descriptionForm',
          'scheduleForm'
        ),
      ...getPropsForTabsWithLanguages()
    };

    return (
      <div>
        <div hidden={selectedLanguage !== DEFAULT_LANGUAGE}>
          <DescriptionCreate
            {...descriptionFormProps}
            isUpdate={isUpdate}
            setSubmitButton={setSubmitButton}
          />
        </div>
        {languagesLength > 1 && (
          <div hidden={selectedLanguage === DEFAULT_LANGUAGE}>
            <DescriptionCreate
              {...descriptionFormProps}
              isUpdate={isUpdate}
              form={`descriptionForm-${secondLanguage}`}
              setSubmitButton={setSubmitButton}
            />
          </div>
        )}
      </div>
    );
  };

  const getPricesSubTabs = () => {
    let subTabs = [];

    if (scheduleActiveSubTab === SCHEDULE_SECTION.dateNoTime) {
      subTabs = [
        {
          title: 'Daily prices',
          instruction: 'Prices per each day, with multiple categories'
        },
        {
          title: 'Equal prices',
          instruction: 'Equal prices for all days with multiple categories'
        }
      ];
    } else if (scheduleActiveSubTab === SCHEDULE_SECTION.dateTime) {
      subTabs = [
        {
          title: 'Prices by days and categories',
          instruction:
            'Prices per day or a group of days, with multiple categories'
        },
        {
          title: 'Equal prices',
          instruction: 'Equal prices for all days with multiple categories'
        }
      ];
    }

    const generalForm = { ...props['generalForm'] };
    if (
      generalForm &&
      generalForm.values &&
      generalForm.values.activityType === 'Private'
    ) {
      if (scheduleActiveSubTab === SCHEDULE_SECTION.pass) {
        subTabs = [
          {
            title: 'Prices by categories',
            instruction: 'Specific prices for different categories'
          }
        ];
      }

      subTabs.unshift({
        title: 'Fixed price',
        instruction: 'One price for whole activity'
      });
    }

    return subTabs;
  };

  const tabs = [
    {
      name: 'General',
      formName: 'generalForm',
      component: getGeneralCreateTab()
    },
    {
      name: 'Description',
      formName: 'descriptionForm',
      component: getDescriptionCreateTab()
    },
    {
      name: 'Schedule',
      formName: 'scheduleForm',
      component: (
        <ScheduleCreate
          onSubmit={(values, formName) => {
            const activityType = props['generalForm'].values.activityType;
            const scheduleTab =
              ACTIVITY_FORMS_ORDER[activeTab][scheduleActiveSubTab];
            let pricesTabIndex =
              activityType === 'Open'
                ? pricesActiveSubTab + 1
                : pricesActiveSubTab;
            if (scheduleTab === 'schedulePassForm') {
              if (activityType === 'Open') {
                pricesTabIndex = 0;
              } else {
                if (pricesActiveSubTab) {
                  pricesTabIndex = 1;
                } else {
                  pricesTabIndex = 0;
                }
              }
              setPricesActiveSubTab(pricesTabIndex);
            }
            setSavedForms({
              ...savedForms,
              [formName]: { ...values },
              scheduleTab: scheduleTab,
              pricesTab:
                ACTIVITY_FORMS_ORDER[ACTIVITY_FORMS_NUMBERS.pricesForm][
                  scheduleActiveSubTab
                ][pricesTabIndex]
            });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
              setIsVisitedPrices(true);
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACTIVITY_FORMS_NUMBERS.pricesForm);
              setIsVisitedPrices(true);
            }
          }}
          getPricesData={(values, formName) => {
            setDynamicInitialData({
              ...dynamicInitialData,
              [formName]: values
            });
          }}
          selectedTabIndex={scheduleActiveSubTab}
          isUpdate={isUpdate}
          setSubmitButton={setSubmitButton}
        />
      ),
      subTabs: [
        {
          title: 'Date, no time',
          instruction: 'Select date & days'
        },
        {
          title: 'Date & time',
          instruction: 'Select both date and time'
        },
        {
          title: 'Pass',
          instruction: 'No date is selected'
        }
      ]
    },
    {
      name: 'Prices',
      formName: 'pricesForm',
      component: (
        <PricesCreate
          onSubmit={(values, formName) => {
            const pricesSubTab =
              props['generalForm'].values.activityType === 'Open'
                ? pricesActiveSubTab + 1
                : pricesActiveSubTab;
            setSavedForms({
              ...savedForms,
              [formName]: { ...values },
              pricesTab:
                ACTIVITY_FORMS_ORDER[activeTab][scheduleActiveSubTab][
                  pricesSubTab
                ]
            });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACTIVITY_FORMS_NUMBERS.activityFuturePricesForm);
            }
          }}
          isOpen={
            props['generalForm'] &&
            props['generalForm'].values.activityType === 'Open'
          }
          scheduleTabIndex={scheduleActiveSubTab}
          selectedTabIndex={pricesActiveSubTab}
          isUpdate={isUpdate}
          setSubmitButton={setSubmitButton}
        />
      ),
      subTabs: getPricesSubTabs()
    },
    {
      name: 'Future Prices',
      formName: 'activityFuturePricesForm',
      component: (
        <ActivityFuturePrices
          onSubmit={values => {
            setSavedForms({ ...savedForms, activityFuturePricesForm: { ...values } });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACTIVITY_FORMS_NUMBERS.addOnsForm);
            }
          }}
          isUpdate={isUpdate}
          setSubmitButton={setSubmitButton}
          existingFutureRatePlanIds={existingFutureRatePlanIds}
          initialValues={futurePricesInitialValues}
        />
      )
    },
    {
      name: 'Add-ons',
      formName: 'addOnsForm',
      component: (
        <AddOns
          {...getPropsForTabsWithLanguages()}
          onSubmit={values => {
            setSavedForms({ ...savedForms, addOnsForm: { ...values } });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACTIVITY_FORMS_NUMBERS.relatedAccommodationsForm);
            }
          }}
          setSubmitButton={setSubmitButton}
          isUpdate={isUpdate}
        />
      )
    },
    {
      name: 'Related Accommodations',
      formName: 'relatedAccommodationsForm',
      component: (
        <RelatedAccommodations
          onSubmit={values => {
            setSavedForms({
              ...savedForms,
              relatedAccommodationsForm: { ...values }
            });
            if (submitButton === 'save' && !isSaveUnsavedChanges) {
              onCreateActivityHandler();
            }
            if (submitButton === 'next' && !isSaveUnsavedChanges) {
              setActiveTab(ACTIVITY_FORMS_NUMBERS.integrationForm);
            }
          }}
          setSubmitButton={setSubmitButton}
          isUpdate={isUpdate}
        />
      )
    },
    {
      name: 'Integration',
      formName: 'integrationForm',
      component: (
        <Integration
          onSubmit={values => {
            setSavedForms({
              ...savedForms,
              integrationForm: { ...values }
            });
            if (submitButton === 'next' && !isSaveUnsavedChanges)
              onCreateActivityHandler();
          }}
          setSubmitButton={setSubmitButton}
        />
      )
    }
  ];

  const getSubActiveTab = () => {
    if (activeTab === ACTIVITY_FORMS_NUMBERS.scheduleForm) {
      return scheduleActiveSubTab;
    } else if (activeTab === ACTIVITY_FORMS_NUMBERS.pricesForm) {
      return pricesActiveSubTab;
    }
  };

  const onSubActiveTabChangeHandler = e => {
    if (activeTab === ACTIVITY_FORMS_NUMBERS.scheduleForm) {
      setScheduleActiveSubTab(parseInt(e.target.value));
    } else if (activeTab === ACTIVITY_FORMS_NUMBERS.pricesForm) {
      setPricesActiveSubTab(parseInt(e.target.value));
    }
  };

  return (
    <>
      <AppHeader />
      <CircularLoader
        isLoading={isLoading}
        loaderClassName={classes.loaderClassName}
      />
      <div className={classes.root}>
        <TabsMenu
          activeTab={activeTab}
          title={
            isUpdate && !isCopy
              ? 'Edit Activity'
              : isCopy
              ? 'Copy Activity'
              : 'Create new Activity'
          }
          isDoneTab={tab => {
            return isActivityFormDone(
              tab.formName,
              languages,
              props,
              savedForms,
              activeTab,
              scheduleActiveSubTab,
              pricesActiveSubTab
            );
          }}
          tabs={tabs}
          change={onActiveTabChangeHandler}
        />

        <RadioBtnTabsMenu
          activeTab={activeTab}
          activeSubTab={getSubActiveTab()}
          tabs={tabs}
          change={onSubActiveTabChangeHandler}
        />

        <div className={classes.content}>
          <div className={classes.contentBox}>
            <TabsPanel tabs={tabs} activeTab={activeTab} />
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => {
  return {
    ...state.form,
    businessOwner: state.businessOwner,
    deletedAddOns: state.activityAndAccommodation.deletedAddOns,
    deletedRelatedAccommodations:
      state.activityAndAccommodation.deletedRelatedAccommodations,
    deletedIntegrations: state.activityAndAccommodation.deletedIntegrations
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (form, field, value) => {
    dispatch(change(form, field, value));
  },
  destroyForm: forms => {
    dispatch(destroy(...forms));
  },
  initializeForm: (form, data) => {
    dispatch(initialize(form, data));
  },
  updateFormErrors: (form, fields) => {
    dispatch(updateSyncErrors(form, fields));
  },
  submitForm: form => {
    dispatch(submit(form));
  },
  clearDeletedFields: () => {
    dispatch(clearDeletedFields());
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateActivity);
